import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { Modal, Spinner, Form } from 'react-bootstrap';
import { BsSearch } from 'react-icons/bs';
import { toast } from 'react-toastify';
import LayoutContainer from 'components/layout/LayoutContainer';
import AdminHeader from 'components/header';
import Card from 'components/inputs/Card';
import Button from 'components/inputs/Button';
import Select from 'components/inputs/Select';
import SearchBox from 'components/general/SearchBox';
import ReactPagination from 'components/general/ReactPagination';
import { API } from 'api';

// Sessions left "in progress" because the charger never sent a clean stop. Operators look at the
// session's meter readings (and the readings around it on the same connector), pick the reading
// that marks the real end, and close it: CPMS re-prices it, refreshes the report row and sends
// ADNOC the completion unless ADNOC already accepted one.

const TZ = 'Asia/Dubai';
const LIMIT = 20;
const fmt = (d) => (d ? moment(d).tz(TZ).format('DD/MM/YYYY HH:mm:ss') : '-');
const openFor = (d) => {
  if (!d) return '-';
  const mins = moment().diff(moment(d), 'minutes');
  if (mins < 60) return `${mins} min`;
  if (mins < 48 * 60) return `${Math.floor(mins / 60)} h ${mins % 60} min`;
  return `${Math.floor(mins / 1440)} days`;
};
const kwhSince = (energyWh, meterStart) =>
  energyWh == null || meterStart == null ? '-' : ((energyWh - meterStart) / 1000).toFixed(2);
const errorMessage = (e) => e?.response?.data?.message || e?.message || 'Request failed';

const ageOptions = [
  { label: 'Any age', value: '' },
  { label: 'Open > 1 hour', value: 60 },
  { label: 'Open > 6 hours', value: 360 },
  { label: 'Open > 24 hours', value: 1440 },
  { label: 'Open > 7 days', value: 10080 },
];

const ReadingsTable = ({ title, note, readings, meterStart, canPick, onPick, busy }) => (
  <div className="mb-4">
    <h6 className="mb-1">
      {title} <small className="text-muted">({readings.length})</small>
    </h6>
    {note && <div className="text-muted small mb-2">{note}</div>}
    {readings.length === 0 ? (
      <div className="text-muted small">No readings found (charger logs are kept for about 25 days).</div>
    ) : (
      <div className="table-responsive">
        <table className="record-list-table">
          <thead>
            <tr>
              <th>Received (GST)</th>
              <th>Charger clock</th>
              <th>Meter (Wh)</th>
              <th>kWh since start</th>
              <th>SoC (%)</th>
              <th>Power (kW)</th>
              <th>OCPP txn</th>
              {canPick && <th />}
            </tr>
          </thead>
          <tbody>
            {readings.map((r) => (
              <tr key={r.logId}>
                <td>{fmt(r.serverTime)}</td>
                <td>{r.chargerTime ? moment(r.chargerTime).tz(TZ).format('DD/MM/YYYY HH:mm:ss') : '-'}</td>
                <td>{r.energyWh ?? '-'}</td>
                <td>{kwhSince(r.energyWh, meterStart)}</td>
                <td>{r.soc ?? '-'}</td>
                <td>{r.powerW == null ? '-' : (r.powerW / 1000).toFixed(1)}</td>
                <td>{r.transactionId ?? '-'}</td>
                {canPick && (
                  <td>
                    <Button className="btn btn-sm" disabled={busy || r.energyWh == null} onClick={() => onPick({ logId: r.logId })}>
                      Use as stop
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

ReadingsTable.propTypes = {
  title: PropTypes.string.isRequired,
  note: PropTypes.string,
  readings: PropTypes.arrayOf(PropTypes.object).isRequired,
  meterStart: PropTypes.number,
  canPick: PropTypes.bool,
  onPick: PropTypes.func,
  busy: PropTypes.bool,
};

const OpenSessions = () => {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [totals, setTotals] = useState({ totalResults: 0, totalPages: 1 });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [olderThan, setOlderThan] = useState('');

  const [selected, setSelected] = useState(null); // session row whose readings are open
  const [readings, setReadings] = useState(null);
  const [readingsLoading, setReadingsLoading] = useState(false);
  const [preview, setPreview] = useState(null); // dry-run result for the picked reading
  const [pick, setPick] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    async (pageVal = 1) => {
      setLoading(true);
      try {
        const params = { page: pageVal, limit: LIMIT };
        if (search.trim()) params.search = search.trim();
        if (olderThan) params.olderThanMinutes = olderThan;
        const res = await API.getOpenSessions(params);
        setRows(res.data.results || []);
        setTotals({ totalResults: res.data.totalResults, totalPages: res.data.totalPages || 1 });
        setPage(pageVal);
      } catch (e) {
        toast.error(errorMessage(e));
      } finally {
        setLoading(false);
      }
    },
    [search, olderThan]
  );

  // Reload when the age filter changes; the search box reloads on submit.
  useEffect(() => {
    load(1);
  }, [olderThan]);

  const openReadings = async (row) => {
    setSelected(row);
    setReadings(null);
    setPreview(null);
    setPick(null);
    setReason('');
    setReadingsLoading(true);
    try {
      const res = await API.getOpenSessionMeterValues(row.transactionId);
      setReadings(res.data);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setReadingsLoading(false);
    }
  };

  const closeModal = () => {
    setSelected(null);
    setReadings(null);
    setPreview(null);
    setPick(null);
  };

  const previewClose = async (choice) => {
    setBusy(true);
    try {
      const res = await API.closeOpenSession(selected.transactionId, { ...choice, dryRun: true });
      setPick(choice);
      setPreview(res.data);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const confirmClose = async () => {
    setBusy(true);
    try {
      const res = await API.closeOpenSession(selected.transactionId, { ...pick, reason });
      const d = res.data;
      toast.success(`Closed ${d.transactionId}: ${d.energyKwh} kWh${d.totalAmount != null ? `, AED ${d.totalAmount}` : ''}. Webhook ${d.webhook}.`);
      if (!d.reportQueued) toast.warn('Report row was not queued; ask support to refresh it.');
      closeModal();
      load(page);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const meterStart = readings?.session?.meterStart;

  return (
    <>
      <LayoutContainer>
        <AdminHeader title="Open Sessions" />
        <div className="page-content-wrapper scrollable">
          <div className="data-report--main">
            <div className="data-report--block">
              <Card>
                <div className="data-report__inner">
                  <div className="d-flex flex-wrap align-items-end justify-content-between mb-3" style={{ gap: 12 }}>
                    <div>
                      <strong>{totals.totalResults}</strong> sessions still in progress
                    </div>
                    <div className="d-flex flex-wrap align-items-end" style={{ gap: 12 }}>
                      <Form
                        onSubmit={(e) => {
                          e.preventDefault();
                          load(1);
                        }}
                      >
                        <SearchBox preIcon={<BsSearch />} value={search} placeholder="Transaction ID" onChange={(e) => setSearch(e.target.value)} />
                      </Form>
                      <div style={{ minWidth: 180 }}>
                        <Select
                          options={ageOptions}
                          sortOptions={false}
                          value={olderThan}
                          placeholder="Any age"
                          onChange={(val) => setOlderThan(val || '')}
                        />
                      </div>
                      <Button onClick={() => load(1)}>Refresh</Button>
                    </div>
                  </div>

                  <div className="table-responsive">
                    <table className="record-list-table">
                      <thead>
                        <tr>
                          <th>Transaction ID</th>
                          <th>Station</th>
                          <th>Charger</th>
                          <th>Connector</th>
                          <th>Started (GST)</th>
                          <th>Open for</th>
                          <th>Meter start (Wh)</th>
                          <th>Last reading (GST)</th>
                          <th>Booking type</th>
                          <th>Customer ID</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan={11} className="text-center">
                              <Spinner animation="border" size="sm" />
                            </td>
                          </tr>
                        ) : rows.length === 0 ? (
                          <tr>
                            <td colSpan={11} className="text-center">
                              No open sessions
                            </td>
                          </tr>
                        ) : (
                          rows.map((r) => (
                            <tr key={r.id}>
                              <td>{r.transactionId}</td>
                              <td>{r.stationCode}</td>
                              <td>{r.chargerId}</td>
                              <td>{r.connectorId}</td>
                              <td>{fmt(r.bookingStart)}</td>
                              <td>{openFor(r.bookingStart)}</td>
                              <td>{r.meterStart ?? '-'}</td>
                              <td>{fmt(r.lastReadingAt)}</td>
                              <td>{r.bookingType}</td>
                              <td>{r.customerId || '-'}</td>
                              <td>
                                <Button className="btn btn-sm" onClick={() => openReadings(r)}>
                                  Readings / close
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  {totals.totalPages > 1 && (
                    <ReactPagination
                      currentPage={page}
                      limit={LIMIT}
                      total={totals.totalResults}
                      handlePageClick={(pageVal) => load(pageVal)}
                      totalPages={totals.totalPages}
                      marginPagesDisplayed={1}
                    />
                  )}
                </div>
              </Card>
            </div>
          </div>
        </div>
      </LayoutContainer>

      <Modal show={!!selected} onHide={closeModal} size="xl" centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title>
            {selected?.transactionId} · {selected?.chargerId} connector {selected?.connectorId}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {readingsLoading || !readings ? (
            <div className="text-center">
              <Spinner animation="border" />
            </div>
          ) : (
            <>
              <div className="mb-3">
                Started {fmt(readings.session.bookingStart)} · meter start <strong>{readings.session.meterStart ?? '-'} Wh</strong> · start SoC{' '}
                {readings.session.startSoc ?? '-'}%
              </div>

              {preview && (
                <Card>
                  <div className="p-3">
                    <h6>Close at this reading?</h6>
                    <div>
                      Stop reading <strong>{preview.meterStop} Wh</strong> at {fmt(preview.bookingStop)} ·{' '}
                      <strong>{preview.energyKwh} kWh</strong> · stop SoC {preview.stopSoc ?? '-'}%
                      {preview.totalAmount != null && (
                        <>
                          {' '}
                          · amount <strong>AED {preview.totalAmount}</strong>
                        </>
                      )}
                    </div>
                    {(preview.warnings || []).map((w) => (
                      <div key={w} className="text-danger small mt-1">
                        {w}
                      </div>
                    ))}
                    <Form.Control
                      className="mt-2"
                      as="textarea"
                      rows={2}
                      placeholder="Reason (optional), e.g. charger rebooted, no StopTransaction"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                    />
                    <div className="d-flex mt-2" style={{ gap: 8 }}>
                      <Button disabled={busy} onClick={confirmClose}>
                        {busy ? 'Closing…' : 'Confirm close'}
                      </Button>
                      <Button className="btn btn-light" disabled={busy} onClick={() => setPreview(null)}>
                        Cancel
                      </Button>
                    </div>
                    <div className="text-muted small mt-2">
                      Confirming completes the session in CPMS, refreshes its report row and sends ADNOC the completion (skipped if ADNOC
                      already accepted one).
                    </div>
                  </div>
                </Card>
              )}

              <ReadingsTable
                title="Before the session"
                note="Last readings on this connector before the session started."
                readings={readings.before}
                meterStart={meterStart}
                canPick={false}
              />
              <ReadingsTable
                title="During the session"
                note={readings.duringTruncated ? 'Showing the latest 500 readings.' : null}
                readings={readings.during}
                meterStart={meterStart}
                canPick
                onPick={previewClose}
                busy={busy}
              />
              <ReadingsTable
                title="After the last session reading"
                note="First readings on this connector after the session's last reading — usually the next session."
                readings={readings.after}
                meterStart={meterStart}
                canPick
                onPick={previewClose}
                busy={busy}
              />

              <h6 className="mb-1">Next session on this connector</h6>
              {readings.nextSession ? (
                <div className="d-flex align-items-center" style={{ gap: 12 }}>
                  <span>
                    {readings.nextSession.transactionId} ({readings.nextSession.status}) started {fmt(readings.nextSession.bookingStart)} · meter start{' '}
                    <strong>{readings.nextSession.meterStart ?? '-'} Wh</strong> · {kwhSince(readings.nextSession.meterStart, meterStart)} kWh since this
                    session started
                  </span>
                  <Button
                    className="btn btn-sm"
                    disabled={busy || readings.nextSession.meterStart == null}
                    onClick={() => previewClose({ nextSession: true })}
                  >
                    Use as stop
                  </Button>
                </div>
              ) : (
                <div className="text-muted small">No later session on this connector yet.</div>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default OpenSessions;
